import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtItalianDate, fmtItalianTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, Save, Calendar as CalendarIcon } from "lucide-react";

export default function EditAppointmentModal({ appt, studios, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(true);

  useEffect(() => {
    if (!appt) return;
    setForm({
      studio_id: appt.studio_id,
      start: appt.start,
      reason: appt.reason,
      first_name: appt.first_name,
      last_name: appt.last_name,
      phone: appt.phone,
      email: appt.email || "",
      note: appt.note || "",
    });
  }, [appt]);

  useEffect(() => {
    if (!open || !form?.studio_id) return;
    setLoading(true);
    // Compute today's Rome date (avoid UTC drift near midnight)
    const romeToday = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    const from_date = romeToday;
    const t = new Date();
    t.setDate(t.getDate() + 30);
    const to_date = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(t);
    api.get("/slots", { params: { studio_id: form.studio_id, from_date, to_date } })
      .then(r => setSlotsByDate(r.data))
      .catch(() => setSlotsByDate({}))
      .finally(() => setLoading(false));
  }, [open, form?.studio_id]);

  const sortedDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  if (!appt || !form) return null;

  const currentIsoUtc = new Date(appt.start).toISOString();
  const isSameAsOriginal = (iso) => new Date(iso).toISOString() === currentIsoUtc && form.studio_id === appt.studio_id;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, notify_patient: notify };
      const { data } = await api.patch(`/admin/appointments/${appt.id}`, payload);
      toast.success("Prenotazione aggiornata");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="edit-appt-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1A231F]">Modifica prenotazione</DialogTitle>
          <p className="text-sm text-[#5C7066]">Attuale: {fmtItalianDate(appt.start)} · {fmtItalianTime(appt.start)}</p>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#2D4A3E]">Studio</Label>
            <select
              data-testid="edit-studio"
              value={form.studio_id}
              onChange={(e) => setForm({ ...form, studio_id: e.target.value })}
              className="h-11 mt-1 w-full rounded-xl border border-[#E0E5E2] bg-white px-3"
            >
              {studios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#2D4A3E]">Nuovo slot (opzionale)</Label>
            <p className="text-xs text-[#5C7066] mb-2">Seleziona uno slot solo se vuoi spostare l'appuntamento. Lo slot attuale è già evidenziato.</p>
            {loading && <div className="text-sm text-[#5C7066]">Caricamento…</div>}
            {!loading && sortedDates.length === 0 && !isSameAsOriginal(form.start) && (
              <div className="text-sm text-[#5C7066]">Nessun altro slot disponibile.</div>
            )}
            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {sortedDates.map((d) => (
                <div key={d}>
                  <div className="flex items-center gap-2 mb-1"><CalendarIcon size={12} className="text-[#2D4A3E]" /><span className="text-xs font-medium text-[#1A231F]">{fmtItalianDate(d + "T12:00:00Z")}</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {slotsByDate[d].map(s => {
                      const active = new Date(s.start).toISOString() === new Date(form.start).toISOString();
                      return (
                        <button
                          key={s.start}
                          data-testid={`edit-slot-${s.start}`}
                          onClick={() => setForm({ ...form, start: s.start })}
                          className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${active ? "bg-[#2D4A3E] text-white border-[#2D4A3E]" : "bg-white border-[#E0E5E2] hover:border-[#8AA399]"}`}
                        >{s.label}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {isSameAsOriginal(form.start) && (
                <div className="text-xs text-[#8AA399]">Slot attuale: {fmtItalianDate(appt.start)} alle {fmtItalianTime(appt.start)}</div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input data-testid="edit-firstname" value={form.first_name} onChange={e=>setForm({...form, first_name:e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
            <div><Label>Cognome</Label><Input data-testid="edit-lastname" value={form.last_name} onChange={e=>setForm({...form, last_name:e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
            <div><Label>Telefono</Label><Input data-testid="edit-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
            <div><Label>Email</Label><Input data-testid="edit-email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
            <div className="sm:col-span-2"><Label>Motivo</Label><Input data-testid="edit-reason" value={form.reason} onChange={e=>setForm({...form, reason:e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
            <div className="sm:col-span-2"><Label>Note</Label><Textarea data-testid="edit-note" rows={3} value={form.note} onChange={e=>setForm({...form, note:e.target.value})} className="mt-1 rounded-xl"/></div>
          </div>

          {form.email && (
            <label className="flex items-start gap-2 text-sm text-[#1A231F]">
              <Checkbox data-testid="edit-notify" checked={notify} onCheckedChange={(v)=>setNotify(!!v)} className="mt-0.5" />
              <span>Invia email di aggiornamento al paziente con il nuovo orario e .ics allegato</span>
            </label>
          )}
        </div>

        <DialogFooter className="pt-4 gap-2">
          <Button data-testid="edit-cancel" variant="ghost" onClick={onClose}><X size={14} className="mr-1"/> Annulla</Button>
          <Button data-testid="edit-save" onClick={save} disabled={saving} className="bg-[#2D4A3E] hover:bg-[#1A2F26]"><Save size={14} className="mr-1"/>{saving ? "Salvataggio…" : "Salva modifiche"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
